import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { ban, sync } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import Routes from 'lib/utils/routes';
import { isRejectableLaborTicket } from 'lib/utils/partAdjustmentTypes';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getLaborFormAllowRejects } from 'lib/selectors/getLaborFormAllowRejects';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getMachine } from 'lib/selectors/getMachine';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import { CategorizeDowntime } from './CategorizeDowntime';
import { SignOutSection } from './SignOutSection';
import { SubmitWorkOrFAISection } from './SubmitWorkOrFAISection';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
} from '../styled';

/**
 * Control bar component for actively running job state.
 *
 * This component is rendered when:
 * - Operator is signed in
 * - Job is currently running
 * - Job is NOT paused
 * - System is NOT in ERP-driven mode
 *
 * Available actions:
 * - Submit Work / FAI — see {@link SubmitWorkOrFAISection}
 * - Reject Parts - Record rejects (each with its own reason) as they happen. Only offered while
 *   the job is running on an open job labor ticket, so every reject lands inside it (a keep-open
 *   close leaves the job open with no ticket; indirect tickets don't report parts); the labor
 *   ticket form pre-fills its Rejects from them.
 * - Switch Activity - Only shown when multiple activity types are available and job is not paused
 * - Categorize Downtime - Categorize downtime intervals
 * - Sign Out / FAI — see {@link SignOutSection}
 *
 * @returns {JSX.Element} Control bar with submit, reject parts, switch activity, downtime categorization, and sign out options
 */
const ActiveJobRunning = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const isPaused = useSelector(getIsPaused);
  const activityTypes = useSelector(getSwitchActivityTypesOptions);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  // Material-picking machines enter good parts only, so they never reject (the labor form
  // hides its rejects for them too).
  const allowRejects = useSelector(getLaborFormAllowRejects);
  const { openSelectActivity } = useSelectActivity();

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <Scrollable>
        <SubmitWorkOrFAISection />

        {allowRejects && isRejectableLaborTicket(currentLaborTicket) && (
          <ControlBarButton
            id="reject-parts"
            onClick={() => {
              return history.push(Routes.machineIdRejectPartsPath(machine.id));
            }}
          >
            <MobileIcon icon={ban} />
            <ResponsiveText
              full={t('rejectParts|full')}
              medium={t('rejectParts|medium')}
              short={t('rejectParts|short')}
            />
          </ControlBarButton>
        )}

        {/* Switch Activity button - only shown when multiple activity types available and not paused */}
        {!isPaused && activityTypes?.length > 1 && (
          <ControlBarButton
            id="job-control-switch-activity"
            onClick={() => {
              return openSelectActivity();
            }}
          >
            <MobileIcon icon={sync} />
            <ResponsiveText
              full={t('switchActivity|full')}
              medium={t('switchActivity|medium')}
              short={t('switchActivity|short')}
            />
          </ControlBarButton>
        )}

        {/* Downtime categorization */}
        <CategorizeDowntime history={history} machine={machine} />

        <SignOutSection />
      </Scrollable>
    </ControlBarResponsive>
  );
};

export { ActiveJobRunning };
